import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputLabel = styled.label`
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: #333;
  margin-bottom: var(--spacing-xs);
  
  ${props => props.required && css`
    &::after {
      content: ' *';
      color: var(--color-error);
    }
  `}
`;

const InputField = styled(motion.input)`
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-base);
  font-family: var(--font-family-primary);
  color: #333;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-fast);
  
  &::placeholder {
    color: #666;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &:hover:not(:disabled) {
    border-color: var(--color-gray-400);
  }
  
  &:disabled {
    background: var(--color-gray-100);
    color: #666;
    cursor: not-allowed;
  }

  /* Size variants */
  ${props => props.size === 'sm' && css`
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--font-size-sm);
  `}
  
  ${props => props.size === 'lg' && css`
    padding: var(--spacing-lg) var(--spacing-xl);
    font-size: var(--font-size-lg);
  `}

  /* Validation states */
  ${props => props.error && css`
    border-color: var(--color-error);
    
    &:focus {
      border-color: var(--color-error);
      box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.1);
    }
  `}
  
  ${props => props.success && css`
    border-color: var(--color-success);
    
    &:focus {
      border-color: var(--color-success);
      box-shadow: 0 0 0 3px rgba(52, 199, 89, 0.1);
    }
  `}

  /* Icon padding */
  ${props => props.hasLeftIcon && css`
    padding-left: calc(var(--spacing-lg) + 24px + var(--spacing-sm));
  `}
  
  ${props => props.hasRightIcon && css`
    padding-right: calc(var(--spacing-lg) + 24px + var(--spacing-sm));
  `}
`;

const TextAreaField = styled(motion.textarea)`
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-base);
  font-family: var(--font-family-primary);
  color: #333;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-fast);
  resize: vertical;
  min-height: 120px;
  
  &::placeholder {
    color: #666;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &:hover:not(:disabled) {
    border-color: var(--color-gray-400);
  }
  
  &:disabled {
    background: var(--color-gray-100);
    color: #666;
    cursor: not-allowed;
  }

  /* Validation states */
  ${props => props.error && css`
    border-color: var(--color-error);
    
    &:focus {
      border-color: var(--color-error);
      box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.1);
    }
  `}
  
  ${props => props.success && css`
    border-color: var(--color-success);
    
    &:focus {
      border-color: var(--color-success);
      box-shadow: 0 0 0 3px rgba(52, 199, 89, 0.1);
    }
  `}
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-text-muted);
  pointer-events: none;
  
  ${props => props.position === 'left' && css`
    left: var(--spacing-md);
  `}
  
  ${props => props.position === 'right' && css`
    right: var(--spacing-md);
  `}
`;

const HelperText = styled.div`
  margin-top: var(--spacing-xs);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  
  ${props => props.error && css`
    color: var(--color-error);
  `}
  
  ${props => props.success && css`
    color: var(--color-success);
  `}
  
  ${props => !props.error && !props.success && css`
    color: var(--color-text-secondary);
  `}
`;

const CharacterCount = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  
  ${props => props.isLimit && css`
    color: var(--color-error);
  `}
`;

const Input = React.forwardRef(({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  error,
  success,
  helperText,
  disabled = false,
  required = false,
  size = 'md',
  leftIcon,
  rightIcon,
  multiline = false,
  rows = 4,
  maxLength,
  autoComplete,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = useState(value || '');

  const handleFocus = (e) => {
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    onBlur?.(e);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;

    if (maxLength && newValue.length > maxLength) {
      return;
    }

    setInternalValue(newValue);
    onChange?.(e);
  };

  const inputValue = value !== undefined ? value : internalValue;
  const isError = error || (maxLength && inputValue.length > maxLength);
  const isSuccess = success && !isError;

  const InputComponent = multiline ? TextAreaField : InputField;

  return (
    <InputWrapper>
      {label && (
        <InputLabel required={required} htmlFor={props.id}>
          {label}
        </InputLabel>
      )}

      <div style={{ position: 'relative' }}>
        {leftIcon && (
          <IconWrapper position="left">
            {leftIcon}
          </IconWrapper>
        )}

        <InputComponent
          ref={ref}
          type={type}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          error={isError}
          success={isSuccess}
          disabled={disabled}
          required={required}
          size={size}
          hasLeftIcon={!!leftIcon}
          hasRightIcon={!!rightIcon}
          rows={multiline ? rows : undefined}
          autoComplete={autoComplete}
          whileFocus={{ scale: 1.02 }}
          {...props}
        />

        {rightIcon && (
          <IconWrapper position="right">
            {rightIcon}
          </IconWrapper>
        )}
      </div>

      {helperText && (
        <HelperText error={isError} success={isSuccess}>
          {helperText}
        </HelperText>
      )}

      {maxLength && (
        <CharacterCount isLimit={inputValue.length > maxLength}>
          {inputValue.length}/{maxLength}
        </CharacterCount>
      )}
    </InputWrapper>
  );
});

Input.displayName = 'Input';

export default Input; 